import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";
import { getSizeLabel } from "../../../utils/getSizeLabel";

const StepReview = ({ formData }) => {
  const { customer, fulfillment, items, payment, internal = {} } = formData;

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Review Order Details
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold">
            Customer Info
          </Typography>
          <Typography>
            Name: {customer.firstName} {customer.lastName}
          </Typography>
          <Typography>Email: {customer.email}</Typography>
          <Typography>Phone: {customer.phone}</Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold">
            Fulfillment
          </Typography>
          <Typography>Method: {fulfillment.method}</Typography>
          <Typography>
            Date:{" "}
            {fulfillment.date
              ? new Date(fulfillment.date).toLocaleDateString()
              : "N/A"}
          </Typography>
          <Typography>Time: {fulfillment.time}</Typography>
          <Typography>Location: {fulfillment.location}</Typography>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" fontWeight="bold" mb={1}>
        Items
      </Typography>

      {items.map((item, i) => {
        const visibleCustomizations = item.customizations?.filter(
          (c) => c.key !== "flavor" && c.key !== "size"
        );

        return (
          <Card key={item._id || i} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight="bold">
                {item.displayName || item.name}
              </Typography>

              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  Qty: {item.quantity}
                </Grid>
                <Grid item xs={6}>
                  Price: ${item.totalPrice.toFixed(2)}
                </Grid>
                {item.flavor?.name && (
                  <Grid item xs={6}>
                    Flavor: {item.flavor.displayName}
                  </Grid>
                )}
                {item.type === "grazeBox" ? (
                  <>
                    {item.selectedSize?.name && (
                      <Grid item xs={6}>
                        Size: {item.selectedSize.name}
                      </Grid>
                    )}
                    {item.selectedDiet?.name && (
                      <Grid item xs={6}>
                        Dietary Restriction: {item.selectedDiet.name}
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      Gift Packaging: {item.giftPackaging ? "Yes" : "No"}
                    </Grid>
                  </>
                ) : (
                  item.size?.name && (
                    <Grid item xs={6}>
                      Size: {getSizeLabel(item.size)}
                    </Grid>
                  )
                )}
              </Grid>

              {visibleCustomizations?.length > 0 && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Customizations:
                  </Typography>
                  {visibleCustomizations.map((c, j) => (
                    <Chip
                      key={j}
                      label={`${c.key}: ${c.value}`}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}

              {item.note?.trim() && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Note:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {item.note}
                  </Typography>
                </Box>
              )}

              {/* 🔵 Main Image */}
              {item.image && (
                <Box mt={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Main Image:
                  </Typography>
                  <Box mt={1}>
                    <img
                      src={item.image}
                      alt="Main"
                      style={{
                        width: "200px",
                        height: "200px",
                        borderRadius: 4,
                        objectFit: "cover",
                        border: "2px solid #1976d2",
                      }}
                    />
                    <br />
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight="bold"
                      textAlign="center">
                      Main Style (Primary)
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* 🟡 Reference Images */}
              {(item.referenceImages || []).length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Reference Images:
                  </Typography>
                  <Grid container spacing={1} mt={1}>
                    {item.referenceImages.map((img, k) => (
                      <Grid item xs={6} sm={4} key={img.url || k}>
                        <img
                          src={img.url}
                          alt={img.purpose || "Reference"}
                          style={{
                            width: "200px",
                            height: "200px",
                            borderRadius: 4,
                            objectFit: "cover",
                          }}
                        />
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {img.purpose === "other" && img.customPurpose
                            ? img.customPurpose
                            : img.purpose || "Unlabeled"}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}

      {internal.kitchenNotes?.trim() && (
        <Card variant="outlined" sx={{ mt: 2, mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              Kitchen Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {internal.kitchenNotes}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold">
            Payment Summary
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1} mt={1}>
            <Grid item xs={6}>
              Subtotal: ${payment.subtotal.toFixed(2)}
            </Grid>
            <Grid item xs={6}>
              Tax: ${payment.tax.toFixed(2)}
            </Grid>
            <Grid item xs={6}>
              Delivery Fee: ${payment.deliveryFee.toFixed(2)}
            </Grid>
            <Grid item xs={6}>
              Discount: ${payment.totalDiscount?.toFixed(2) || "0.00"}
            </Grid>
            <Grid item xs={6}>
              Paid: {payment.paid ? "Yes" : "No"}
            </Grid>
            <Grid item xs={6}>
              Status: {payment.paymentStatus}
            </Grid>
            <Grid item xs={6}>
              Refund Status: {payment.refundStatus}
            </Grid>
            <Grid item xs={6}>
              Currency: {payment.currency}
            </Grid>
            <Grid item xs={6}>
              Receipt #: {payment.receiptNumber}
            </Grid>
            <Grid item xs={6}>
              Payment Method: {payment.paymentMethod?.type || "N/A"}
            </Grid>
            <Grid item xs={6}>
              Card: {payment.paymentMethod?.brand || "-"}{" "}
              {payment.paymentMethod?.last4
                ? `•••• ${payment.paymentMethod.last4}`
                : ""}
            </Grid>
            <Grid item xs={6} fontWeight="bold">
              Total: ${payment.total.toFixed(2)}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StepReview;
